import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { authApiService } from "../../../../api/api";
import { useUser } from "../../../../providers/UserProvider";
import {
  IBatchIdsWriteOff,
  IOrder,
  IWriteOffProductsOrder,
} from "../../../../types/order.type";
import { IProduct } from "../../../../types/product.type";
import { ISelectsInput } from "../../../../types/selects.type";
import BlueButton from "../../../buttons/BlueButton/BlueButton";
import { formatDate, toDatetimeLocalValue } from "../../../../utils/formatDate";
import ResourceCountButton from "../../../buttons/ResourceCountButton/ResourceCountButton";
import SelectChangeProdListInput from "../../../inputs/SelectChangeProdListInput/SelectChangedProdListInput";
import ModalBottomButtons from "../buttons/ModakBottomButtons/ModalBottomButtons";
import EditInput from "../inputs/EditInput/EditInput";
import styles from "./styles.module.scss";

interface Props {
  onClose: () => void;
  onApply: () => void;
  products: IProduct[];
  order: IOrder;
}

const EditOrderForm: React.FC<Props> = (props) => {
  const user = useUser();
  const [search, setSearch] = useState<string>("");
  const [selectedProducts, setSelectedProducts] = useState<ISelectsInput[]>([]);
  const [invoice, setInvoice] = useState<string>(props.order.invoice ?? "");
  const [orderDateLocal, setOrderDateLocal] = useState<string>(
    toDatetimeLocalValue(props.order.createdAt)
  );

  useEffect(() => {
    setInvoice(props.order.invoice ?? "");
    setOrderDateLocal(toDatetimeLocalValue(props.order.createdAt));
  }, [props.order]);

  const getWarehouse = () => {
    const result: ISelectsInput[] = [];
    for (const product of props.products) {
      for (const batch of product.batches) {
        if (batch.batchQuantity > 0) {
          result.push({
            id: batch.id,
            name: product.name,
            subName: product.capacity,
            secondSubName: `${batch.batchName}/${formatDate(batch.batchDate)}`,
            count: batch.batchQuantity,
            selectCount: 0,
            resourceId: product.id,
          });
        }
      }
    }
    return result;
  };

  const onPressWithdraw = async () => {
    try {
      const process = await authApiService.createProccessing({
        orderId: props.order.id,
      });

      if (process) {
        const batchIds: IBatchIdsWriteOff[] = [];
        for (const item of selectedProducts) {
          if (item.selectCount && item.selectCount > 0) {
            batchIds.push({ batchId: item.id, quantity: item.selectCount });
          }
        }

        if (user.user?.id) {
          const body: IWriteOffProductsOrder = {
            processingId: process.id,
            batchIds,
            userId: user.user.id,
          };

          const response = await authApiService.writeOffOrder(body);

          if (response) {
            props.onApply();
            props.onClose();
          }
        }
      }
    } catch (err: any) {
      console.error(err);
      toast.error(err.message);
    }
  };

  const onSaveInvoiceAndDate = async () => {
    if (!orderDateLocal) {
      toast.error("Вкажіть дату замовлення");
      return;
    }
    try {
      const createdAt = new Date(orderDateLocal).toISOString();
      await authApiService.updateOrderStatus(props.order.id.toString(), {
        invoice: invoice.trim(),
        createdAt,
      });
      toast.success("Дані замовлення оновлено");
      props.onApply();
    } catch (err: any) {
      console.error(err);
      toast.error(
        err.response?.data?.message || err.message || "Не вдалося зберегти"
      );
    }
  };

  return (
    <div className={styles.container}>
      <div className={`${styles.headerContainer}`}></div>
      <div className={styles.metaEditSection}>
        <EditInput
          title="Номер інвойса"
          value={invoice}
          setValue={setInvoice}
          isRequired={false}
        />
        <div className={styles.datetimeRow}>
          <label htmlFor="order-created-at-edit" className={styles.datetimeLabel}>
            Дата замовлення
          </label>
          <input
            id="order-created-at-edit"
            type="datetime-local"
            className={styles.datetimeInput}
            value={orderDateLocal}
            onChange={(e) => setOrderDateLocal(e.target.value)}
          />
        </div>
        <BlueButton
          title="Зберегти інвойс і дату"
          onClick={onSaveInvoiceAndDate}
          isBigButton
        />
      </div>
      <div className={styles.formContainer}>
        <form className={styles.form}>
          <SelectChangeProdListInput
            items={getWarehouse()}
            title="Warehouse"
            values={selectedProducts}
            onClickActive={(value) => {
              setSelectedProducts((prevState) =>
                prevState.filter((item) => item.id !== value.id)
              );
            }}
            onClickInactive={(value) =>
              setSelectedProducts((prevState) => [...prevState, value])
            }
            onChangeCount={(id, value) => {
              const findIndex = selectedProducts.findIndex(
                (item) => item.id === id
              );
              if (findIndex !== -1) {
                const newState = [...selectedProducts];
                newState[findIndex].selectCount = value;
                setSelectedProducts(newState);
              }
            }}
            search={search}
            setSearch={(value) => setSearch(value)}
            isMultiSelect={true}
          />
        </form>
        <form className={styles.formSelect}>
          {props.order.items.map((item) => (
            <ResourceCountButton
              name={item.product.name}
              capacity={item.product.capacity}
              needCount={item.quantity}
              count={(() => {
                const res = selectedProducts.filter(
                  (i) => i.resourceId === item.product.id
                );

                return res.reduce((acc, i) => {
                  if (i.selectCount) {
                    return (acc += i.selectCount);
                  }
                  return acc;
                }, 0);
              })()}
              isActive={true}
              onClick={(value) => setSearch(value)}
            />
          ))}
        </form>
      </div>

      <ModalBottomButtons
        onPressApply={onPressWithdraw}
        applyTitle="Withdraw"
        onPressBack={props.onClose}
        backTitle="Back"
        isApplyDisabled={(() => {
          if (selectedProducts.length === 0) {
            return true;
          }
          console.log(selectedProducts);
          const res = props.order.items.map((item) => ({
            id: item.product.id,
            quantity: item.quantity,
          }));
          return !res.every(
            (item) =>
              selectedProducts.find((i) => i.resourceId === item.id)
                ?.selectCount === item.quantity
          );
        })()}
      />
    </div>
  );
};

export default EditOrderForm;
